/**
 * src/components/map/mapOverlayStore.tsx
 *
 * Who is open over the map right now.
 *
 * Plumbing only. The policy — which surface yields, what may never be
 * covered, how many may be open on a phone — lives in `mapSurfaces`,
 * where it is pure and testable. This file collects what surfaces
 * report about themselves and hands it back to whoever asks.
 *
 * Context rather than a store library because the application has no
 * store library and this is not the change that should introduce one.
 * `MapDataContext` established the pattern next door; this follows it.
 *
 * Two details do real work:
 *
 *   **A no-op default.** Used outside a provider, every hook degrades
 *   to "no surfaces are open" instead of throwing. The map preview
 *   page, a unit test rendering one card, and any future host all keep
 *   working, and a caller that forgot the provider gets the old
 *   behaviour rather than a crash.
 *
 *   **Identical rectangles are not updates.** A card reads its avoid
 *   zones, places itself, then reports where it landed. If reporting
 *   the same rectangle counted as a change, that would re-render the
 *   card, which would place itself again, which would report again.
 *   The reducer compares and returns the existing state, which is what
 *   stops the loop.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef,
} from 'react';
import type { Rect } from './mapTooltipPlacement';
import {
  avoidZonesFor, openMajorSurface, type MapSurface, type MapSurfaceKind,
} from './mapSurfaces';

/**
 * The reducer and its state are exported because that is where the
 * behaviour worth testing lives, and because `mapHoverLifecycle` next
 * door established the shape: a pure reducer that a component wires
 * up, so timing and identity rules can be checked without a DOM.
 */
export interface MapOverlayState {
  /** Keyed by surface id; order is not meaningful. */
  surfaces: Record<string, MapSurface>;
}

export type MapOverlayAction =
  | { type: 'open'; id: string; kind: MapSurfaceKind; rect: Rect | null }
  | { type: 'measure'; id: string; rect: Rect | null }
  | { type: 'close'; id: string };

export function initialOverlayState(): MapOverlayState {
  return { surfaces: {} };
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}

export function mapOverlayReducer(
  state: MapOverlayState,
  action: MapOverlayAction,
): MapOverlayState {
  switch (action.type) {
    case 'open': {
      const existing = state.surfaces[action.id];
      if (existing && existing.kind === action.kind && sameRect(existing.rect, action.rect)) {
        return state;
      }
      return {
        surfaces: {
          ...state.surfaces,
          [action.id]: { id: action.id, kind: action.kind, rect: action.rect },
        },
      };
    }

    case 'measure': {
      const existing = state.surfaces[action.id];
      // A measurement for a surface that has closed is discarded rather
      // than resurrecting it: a card reporting its rectangle on the way
      // out would otherwise leave a permanent hole in the map.
      if (!existing) return state;
      if (sameRect(existing.rect, action.rect)) return state;
      return {
        surfaces: { ...state.surfaces, [action.id]: { ...existing, rect: action.rect } },
      };
    }

    case 'close': {
      if (!state.surfaces[action.id]) return state;
      const next = { ...state.surfaces };
      delete next[action.id];
      return { surfaces: next };
    }

    default:
      return state;
  }
}

export interface MapOverlayApi {
  /** Every surface currently open, including unmeasured ones. */
  surfaces: readonly MapSurface[];
  openSurface: (id: string, kind: MapSurfaceKind, rect?: Rect | null) => void;
  measureSurface: (id: string, rect: Rect | null) => void;
  closeSurface: (id: string) => void;
}

const EMPTY: readonly MapSurface[] = Object.freeze([]);

const NOOP: MapOverlayApi = {
  surfaces: EMPTY,
  openSurface: () => {},
  measureSurface: () => {},
  closeSurface: () => {},
};

const MapOverlayContext = createContext<MapOverlayApi>(NOOP);

export const MapOverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(mapOverlayReducer, initialOverlayState());

  const openSurface = useCallback(
    (id: string, kind: MapSurfaceKind, rect: Rect | null = null) =>
      dispatch({ type: 'open', id, kind, rect }),
    [],
  );
  const measureSurface = useCallback(
    (id: string, rect: Rect | null) => dispatch({ type: 'measure', id, rect }),
    [],
  );
  const closeSurface = useCallback((id: string) => dispatch({ type: 'close', id }), []);

  const surfaces = useMemo(() => Object.values(state.surfaces), [state.surfaces]);

  const api = useMemo<MapOverlayApi>(
    () => ({ surfaces, openSurface, measureSurface, closeSurface }),
    [surfaces, openSurface, measureSurface, closeSurface],
  );

  return <MapOverlayContext.Provider value={api}>{children}</MapOverlayContext.Provider>;
};

export function useMapOverlays(): MapOverlayApi {
  return useContext(MapOverlayContext);
}

/**
 * Register a surface for as long as it is mounted, and report where it
 * is.
 *
 * The rectangle is reported after the fact, because a card cannot know
 * where it is until it has been placed, and placement depends on what
 * it must avoid. That ordering is only safe because a surface never
 * avoids itself and never avoids anything it outranks: a card moving
 * can shift lower-ranked cards, and those cannot shift it back.
 *
 * Returns the zones this surface must keep clear of, so the common case
 * is a single call.
 */
export function useMapSurface(input: {
  id: string;
  kind: MapSurfaceKind;
  /** Where it landed, once known. */
  rect?: Rect | null;
  /** Permanent host furniture, avoided regardless of rank. */
  furniture?: readonly Rect[];
  /** Skip registration entirely, e.g. while the surface is closed. */
  enabled?: boolean;
}): { avoid: Rect[]; surfaces: readonly MapSurface[] } {
  const {
    id, kind, rect = null, furniture, enabled = true,
  } = input;
  const { surfaces, openSurface, measureSurface, closeSurface } = useMapOverlays();

  useEffect(() => {
    if (!enabled) return undefined;
    openSurface(id, kind);
    return () => closeSurface(id);
  }, [enabled, id, kind, openSurface, closeSurface]);

  // Kept in a ref so the effect below depends on the rectangle's
  // values rather than on the identity of a fresh object literal,
  // which a parent re-render produces every time.
  const rectRef = useRef<Rect | null>(rect);
  rectRef.current = rect;
  const rectKey = rect ? `${rect.left},${rect.top},${rect.right},${rect.bottom}` : '';

  useEffect(() => {
    if (!enabled) return;
    measureSurface(id, rectRef.current);
  }, [enabled, id, rectKey, measureSurface]);

  const avoid = useMemo(
    () => avoidZonesFor({ surfaces, self: { id, kind }, furniture }),
    [surfaces, id, kind, furniture],
  );

  return { avoid, surfaces };
}

/**
 * The major surface already open, for the one-at-a-time rule on narrow
 * viewports.
 */
export function useOpenMajorSurface(exceptId?: string): MapSurface | null {
  const { surfaces } = useMapOverlays();
  return useMemo(() => openMajorSurface(surfaces, exceptId), [surfaces, exceptId]);
}

export default MapOverlayProvider;
